import dynamic from 'next/dynamic'
import { Row, Container, Spacer } from '@smuchile/pkg-unimarc-components'
import {
  BigScreen,
  SmallScreen
} from '@smuchile/pkg-unimarc-components/helpers'
import { Products } from './Products'
import { Totalizers } from './Totalizers'
import { useState } from 'react'
import { BodyLoader } from '../BodyLoader'
import { ICart } from '@smuchile/pkg-unimarc-hooks/shared/interfaces/INewCartUnimarc'
import { OfferYouCarouselProps } from '../OfferYou'
import {
  useMobile,
  useSimulationsForPayments
} from '@smuchile/pkg-unimarc-hooks'
import { UNIPAY_ID } from '@smuchile/pkg-unimarc-hooks/shared/constants'
import { useSavingsData } from 'shared/hooks/useSavingsData'
import { useIndexingMembership } from 'shared/hooks/useIndexingMembership'

const DynamicOfferYou = dynamic<OfferYouCarouselProps>(() => {
  return import('../OfferYou').then((module) => {
    return module.OfferYouCarousel
  })
})

export interface ProductsTotalizersProps {
  cartData: ICart
  createListButton: boolean
  isFetchingCart: boolean
  isLoadingByEvent: boolean
  isLoadingCart: boolean
  isLoadingMyUsualsAndFavorites: boolean
  isLoadingOrderForm: boolean
  notificationAddToList: boolean
  objetSrcImagesIconAddress: {
    imgByke: string
    imgStore: string
  }
  openCollapse: boolean
  openCollapseIssues: boolean
  openMyUsuals: boolean
  selectedListName: string
  handleOpenMyUsualsAndFavorites: () => void
  handleOpenMyListsModal: () => void
  handleOnClosed: () => void
  setNotificationAddToList: (type: boolean) => void
  setOpenCollapse: React.Dispatch<React.SetStateAction<boolean>>
  setOpenCollapseIssues: React.Dispatch<React.SetStateAction<boolean>>
}

export const ProductsTotalizers = ({
  cartData,
  createListButton,
  isFetchingCart,
  isLoadingByEvent,
  isLoadingCart,
  isLoadingMyUsualsAndFavorites,
  isLoadingOrderForm,
  notificationAddToList,
  objetSrcImagesIconAddress,
  openCollapse,
  openCollapseIssues,
  openMyUsuals,
  selectedListName,
  handleOpenMyUsualsAndFavorites,
  handleOpenMyListsModal,
  handleOnClosed,
  setNotificationAddToList,
  setOpenCollapse,
  setOpenCollapseIssues
}: ProductsTotalizersProps): React.ReactElement => {
  const [limitOnProductAlert, setLimitOnProductAlert] = useState(false)
  const { innerWidth } = useMobile()
  const isMobile = innerWidth < 1280

  const {
    data: simulationData,
    refetch: refetchSimulation,
    isFetching: isFetchingSimulation
  } = useSimulationsForPayments({
    payments: [UNIPAY_ID]
  })

  // membership savings
  const {
    handleSavingsToast,
    itemFromCF,
    refetchSavings,
    savings,
    showSavings,
    showSavingsToast
  } = useSavingsData()

  const { showIndexingMembership, showToast, handleCloseToast } =
    useIndexingMembership()

  const toastMembershipProps = {
    handleCloseToast,
    handleSavingsToast,
    item: itemFromCF,
    savings,
    showIndexingMembership,
    showSavings,
    showSavingsToast,
    showToast
  }

  if (!cartData || isLoadingCart || isLoadingOrderForm) {
    return <BodyLoader />
  }

  return (
    <Container isWrap>
      <Row tagName='section'>
        <Products
          cartData={cartData}
          isLoadingByEvent={isLoadingByEvent}
          objetSrcImagesIconAddress={objetSrcImagesIconAddress}
          openCollapse={openCollapse}
          openCollapseIssues={openCollapseIssues}
          setLimitOnProductAlert={setLimitOnProductAlert}
          setOpenCollapse={setOpenCollapse}
          setOpenCollapseIssues={setOpenCollapseIssues}
        />
        <Totalizers
          cartData={cartData}
          createListButton={createListButton}
          handleOnClosed={handleOnClosed}
          handleOpenMyListsModal={handleOpenMyListsModal}
          handleOpenMyUsualsAndFavorites={handleOpenMyUsualsAndFavorites}
          isFetchingCart={isFetchingCart}
          isFetchingSimulation={isFetchingSimulation}
          isLoadingByEvent={isLoadingByEvent}
          isLoadingCart={isLoadingCart}
          isLoadingMyUsualsAndFavorites={isLoadingMyUsualsAndFavorites}
          limitOnProductAlert={limitOnProductAlert}
          notificationAddToList={notificationAddToList}
          openMyUsuals={openMyUsuals}
          refetchSavings={refetchSavings}
          refetchSimulation={refetchSimulation}
          selectedListName={selectedListName}
          setLimitOnProductAlert={setLimitOnProductAlert}
          setNotificationAddToList={setNotificationAddToList}
          simulationData={simulationData}
          toastMembership={{ ...toastMembershipProps }}
        />
      </Row>
      <BigScreen>
        <Spacer.Horizontal size={12} />
      </BigScreen>
      <Row
        padding={isMobile ? '0px 0px 0px 7.5px' : '0'}
        tagName='section'
      >
        <DynamicOfferYou cartData={cartData} />
      </Row>
      <SmallScreen>
        <>
          {showSavings ? (
            <Spacer.Horizontal customSize={220} />
          ) : (
            <Spacer.Horizontal customSize={148} />
          )}
        </>
      </SmallScreen>
    </Container>
  )
}
